import { useCallback, useMemo } from "react";
import { useSelector } from "react-redux";

import { getProjectLayers } from "../../ducks/project/selectors";
import { getTableFilterConditions } from "../../ducks/table/selectors";
import { getDateFilterConditions } from "../../ducks/dateFilter/selectors";
import { getHiddenLegends } from "../../ducks/style/selectors";
import { useIsDashboard } from "../location/useIsDashboard";
import { useGeometrySelectionConditions } from "../geometrySelection/useGeometrySelectionConditions";
import { useProjectLayersToRender } from "../project/useProjectLayers";
import { useDataSourceCondition } from "./useDataSourceCondition";

import { FilterConditionOperation } from "../../components/LayerFilter/types";

export const useTempLayerConditions = (checkFiltering = true, checkHiddenLegends = false): Record<string, string> => {
  const isDashboard = useIsDashboard();
  const layers = useProjectLayersToRender(!isDashboard);
  const currentProjectLayers = useSelector(getProjectLayers);
  const hiddenLegends = useSelector(getHiddenLegends);
  const geometrySelectionConditions = useGeometrySelectionConditions();
  const tableFilterConditions = useSelector(getTableFilterConditions);
  const currentDateFilterCondition = useSelector(getDateFilterConditions);
  const getDashboardCondition = useDataSourceCondition();

  const getCurrentFilters = useCallback(
    (layerName: string) =>
      isDashboard
        ? [!checkFiltering ? geometrySelectionConditions?.[layerName] : undefined, ...getDashboardCondition(layerName)]
        : [
            currentProjectLayers?.[layerName]?.condition,
            currentDateFilterCondition?.[layerName],
            !checkFiltering ? tableFilterConditions?.[layerName] : undefined,
            !checkFiltering ? geometrySelectionConditions?.[layerName] : undefined,
            ...(checkHiddenLegends && hiddenLegends[layerName]?.length ? hiddenLegends[layerName] : []),
          ],
    [
      checkFiltering,
      checkHiddenLegends,
      currentDateFilterCondition,
      currentProjectLayers,
      geometrySelectionConditions,
      getDashboardCondition,
      hiddenLegends,
      isDashboard,
      tableFilterConditions,
    ],
  );

  return useMemo(
    () =>
      layers?.reduce(
        (acc: Record<string, string>, curr) => ({
          ...acc,
          [curr.name]: getCurrentFilters(curr.name)
            .filter((item, index, array) => item && array.indexOf(item) === index)
            .join(` ${FilterConditionOperation.AND} `),
        }),
        {},
      ),
    [getCurrentFilters, layers],
  );
};
